/// <amd-module name="WorkItemsHub/Utils/ColumnOptionsPanelLauncher" />
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import * as Utils_String from "VSS/Core/Util/String";
import { ActionsCreator } from "WorkItemsHub/Actions/ActionsCreator";
import { WorkItemsHubTabs, MentionedTabDataProvider, RecentActivityConstants } from "WorkItemsHub/Generated/Constants";
import { WorkItemsHubSortOption, WorkItemsHubColumnOption } from "Generated/Contracts";
import * as Resources from "WorkItemsHub/Resources";
import { updateColumnSettings } from "WorkItemsHub/Utils/WorkItemsHubTabSettings";
import * as WorkItemsHubTabUtils from "WorkItemsHub/Utils/WorkItemsHubTabUtils";
import { LegacyFieldDefinition, IColumnOptionsResult } from "./ColumnOptionsPanelContracts";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { UsageTelemetryHelper, TelemetryConstants } from "WorkItemsHub/Utils/Telemetry";
import { traceError } from "VSS/Platform/Trace";

/**
 * Utility to launch column options panel with custom available fields for work items hub
 */
export class ColumnOptionsPanelLauncher {
    /**
     * Version
     */
    private _version: number;

    private _projectId: string;

    private _tabId: string;

    constructor(private _actionCreator: ActionsCreator, private _pageContext: IVssPageContext) {
        const tfsPageService = _pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        this._projectId = tfsPageData.project.id;
    }

    public openColumnOptionPanel(tabId: string, currentColumns: WorkItemsHubColumnOption[], sortOptions: WorkItemsHubSortOption[], version: number) {
        const displayColumns = currentColumns.map(column => ({
            fieldRefName: column.fieldReferenceName,
            width: column.width
        }));

        const sortColumns = sortOptions.map(option => ({
            fieldRefName: option.fieldReferenceName,
            descending: !option.isAscending
        }));

        this._version = version;
        this._tabId = tabId;

        const panelProps = {
            getFieldWidth: this._getFieldWidth,
            getAvailableFields: this._getAvailableFields,
            displayColumns,
            sortColumns,
            allowSort: true,
            project: this._projectId
        };

        const layoutManager = this._pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss: () => void) => {
            return React.createElement(
                LegacyComponent,
                {
                    modules: ["WorkItemTracking/Scripts/Queries/Components/ColumnOptions/ColumnOptionsPanel"],
                    wrappedType: "columnOptionsPanel",
                    ...panelProps,
                    onOkClick: (newColumns, dataChanged?) => {
                        onDismiss();
                        this._onOkClick(newColumns, dataChanged);
                    },
                    onDismiss
                },
                null
            );
        });
    }

    private _onOkClick = (result: IColumnOptionsResult, dataChanged?: boolean): void  => { 
        if (!dataChanged) {
            return;
        }

        // persist settings
        const columnOptions = result.display.map(
            column =>
                ({
                    fieldReferenceName: column.name,
                    width: column.width
                } as WorkItemsHubColumnOption)
        );

        const sortOptions = result.sort.map(
            column =>
                ({
                    fieldReferenceName: column.name,
                    isAscending: column.asc
                } as WorkItemsHubSortOption)
        );

        updateColumnSettings(this._pageContext, this._projectId, this._tabId, columnOptions, sortOptions, this._version).then(
            () => {
                // refresh grid data only after setting was persisted otherwise we may get old column settings
                this._actionCreator.invalidateTabData(this._tabId);
                this._actionCreator.refreshDataProviderAsync(this._tabId);

                UsageTelemetryHelper.publishColumnOptionsChange(this._pageContext, this._tabId, {
                    columns: result.display.map(value => value.name).join(),
                    sortOptions: result.sort.map(value => value.name).join(),
                    addedColumns: result.added.join(),
                    removedColumns: result.removed.join(),
                    addedColumnsCount: result.added.length,
                    removedColumnsCount: result.removed.length
                });
            },
            reason => {
                traceError(
                    this._pageContext,
                    TelemetryConstants.Area,
                    TelemetryConstants.ColumnOptionsChange,
                    `Failed to store column settings for work items hub ${this._tabId}, columns: ${columnOptions.length}, sort: ${
                        sortOptions.length
                    }, ${reason}`
                );
            }
        );
    }

    private _getAvailableFields = (): Promise<LegacyFieldDefinition[]>  => { 
        // provide a unique FieldId for each field
        // This id will only be used for organizing field objects inside the ColumnOptionsPanel
        let maxFieldId = 0;
        const returnedFields: LegacyFieldDefinition[] = [];

        const client: IWorkItemTrackingRestClient = this._pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
        return client.getFields(this._projectId).then(
            allFields => {
                for (const field of allFields) {
                    // For now, filter out the 'Parent' field
                    if (!Utils_String.equals(field.referenceName, CoreFieldRefNames.Parent, true)) {
                        const sortable = Utils_String.equals(field.referenceName, CoreFieldRefNames.Tags, true) ? true : field.canSortBy;
                        returnedFields.push(this._createWorkItemsHubField(maxFieldId++, field.referenceName, field.name, sortable, field.isQueryable));
                    }
                }
                const additionalFields: LegacyFieldDefinition[] = this._getAdditionalField(maxFieldId);
                if (additionalFields) {
                    returnedFields.push(...additionalFields);
                }
                return returnedFields;
            },
            error => Promise.reject(error)
        );
    }

    private _getAdditionalField = (maxFieldId: number): LegacyFieldDefinition[]  => { 
        const fields: LegacyFieldDefinition[] = [];
        switch (WorkItemsHubTabUtils.TabEnumValueByTabIdMap[this._tabId]) {
            case WorkItemsHubTabs.Mentioned:
                fields.push(
                    this._createWorkItemsHubField(++maxFieldId, MentionedTabDataProvider.MentionedDateField, Resources.MentionedDateColumnDisplayName)
                );
                break;

            case WorkItemsHubTabs.MyActivity:
                fields.push(
                    this._createWorkItemsHubField(
                        ++maxFieldId,
                        RecentActivityConstants.MyActivityDetailsField,
                        Resources.MyActivityDetailsColumnDisplayName
                    )
                );
                fields.push(
                    this._createWorkItemsHubField(++maxFieldId, RecentActivityConstants.MyActivityDateField, Resources.ActivityDateColumnDisplayName)
                );
                break;

            case WorkItemsHubTabs.RecentlyUpdated:
                fields.push(
                    this._createWorkItemsHubField(
                        ++maxFieldId,
                        RecentActivityConstants.RecentlyUpdatedDateField,
                        Resources.ActivityDateColumnDisplayName
                    )
                );
                break;

            default:
                return null;
        }

        return fields;
    }

    private _createWorkItemsHubField(
        id: number,
        fieldReferenceName: string,
        fieldDisplayName: string,
        canSort: boolean = true,
        canQuery: boolean = true
    ): LegacyFieldDefinition {
        const def = new LegacyFieldDefinition();
        def.id = id;
        def.name = fieldDisplayName;
        def.referenceName = fieldReferenceName;
        def.canSort = canSort;
        def.canQuery = canQuery;

        return def;
    }

    private _getFieldWidth = (fieldRefName: string): number  => { 
        // Provide default width for added display fields
        return 150;
    }
}
